import { useMemo, useState } from"react";
import { Search, BookOpen, X, ChevronDown, ChevronUp, Lightbulb, AlertTriangle, Calculator } from"lucide-react";
import { GLOSSAIRE, GLOSSAIRE_INDEX } from"@/data/glossaire";

const CAT_COLORS = {"Mécanique de base":"#2D6BE0","Structure des états":"#A33B4B","Immobilisations & temps":"#A56A2E","Tiers, TVA & trésorerie":"#2F7A5C","Plan Comptable Général":"#3B1E6E","Analyse financière":"#15294F",
};

function norm(s) {
 return s
 .toLowerCase()
 .normalize("NFD")
 .replace(/[\u0300-\u036f]/g,"");
}

// Surligne les codes comptes entre backticks et le **gras** dans le mini-cas
function renderRich(text, color) {
 if (!text) return null;
 const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\d{2,6}(?=\s|\.|,|\/|$))/);
 return parts.map((p, i) => {
 if (p.startsWith("**") && p.endsWith("**")) {
 return <strong key={i} className="font-bold" style={{ color }}>{p.slice(2, -2)}</strong>;
 }
 if (p.startsWith("`") && p.endsWith("`")) {
 return <code key={i} className="px-1 py-0.5 rounded bg-slate-100 font-mono text-[0.85em] font-bold text-[#15294F]">{p.slice(1, -1)}</code>;
 }
 return <span key={i}>{p}</span>;
 });
}

export default function Glossaire() {
 const [q, setQ] = useState("");
 const [openId, setOpenId] = useState(null);

 const filtered = useMemo(() => {
 const nq = norm(q.trim());
 if (!nq) return GLOSSAIRE;
 return GLOSSAIRE.map((cat) => ({
 ...cat,
 terms: cat.terms.filter(
 (t) =>
 norm(t.terme).includes(nq) ||
 norm(t.def).includes(nq) ||
 (t.ex && norm(t.ex).includes(nq)) ||
 (t.dev && norm(t.dev).includes(nq)) ||
 (t.piege && norm(t.piege).includes(nq)) ||
 (t.cas && norm(t.cas).includes(nq))
 ),
 })).filter((cat) => cat.terms.length > 0);
 }, [q]);

 const total = GLOSSAIRE_INDEX.length;
 const shown = filtered.reduce((acc, c) => acc + c.terms.length, 0);

 return (
 <div data-testid="glossaire-view" className="space-y-8">
 {/* Hero */}
 <div className="bg-gradient-to-br from-white to-slate-50 border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-start gap-4">
 <div className="shrink-0 w-12 h-12 rounded-xl bg-[#2D6BE0]/10 border border-[#2D6BE0]/30 flex items-center justify-center">
 <BookOpen className="text-[#2D6BE0]" size={22} />
 </div>
 <div className="flex-1">
 <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full border border-[#2D6BE0]/30 bg-blue-50 text-[#2D6BE0] mb-3">
 Mémo de poche
 </span>
 <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#15294F] leading-tight">
 Glossaire essentiel
 </h1>
 <p className="mt-3 text-slate-600 max-w-2xl">
 Les termes à connaître absolument avant d&apos;attaquer le plan
 comptable. <strong>{total} définitions</strong> classées par
 thème, écrites simplement.
 </p>
 </div>
 </div>

 {/* Search */}
 <div className="mt-6 relative max-w-xl">
 <Search
 size={16}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
 />
 <input
 data-testid="glossaire-search"
 value={q}
 onChange={(e) => setQ(e.target.value)}
 placeholder="Rechercher un terme (ex. amortissement, TVA…)"
 className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-keyto bg-white text-sm font-medium text-[#15294F] focus:outline-none focus:ring-2 focus:ring-[#2D6BE0]/30 focus:border-[#2D6BE0]"
 />
 {q && (
 <button
 data-testid="glossaire-search-clear"
 onClick={() => setQ("")}
 className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
 >
 <X size={16} />
 </button>
 )}
 </div>

 {q && (
 <p
 data-testid="glossaire-result-count"
 className="mt-2 text-xs text-slate-500"
 >
 {shown} terme{shown > 1 ?"s" :""} trouvé{shown > 1 ?"s" :""}
 </p>
 )}
 </div>

 {/* Catégories */}
 {filtered.length === 0 && (
 <div
 data-testid="glossaire-empty"
 className="border border-dashed border-keyto rounded-xl bg-white p-8 text-center text-slate-500"
 >
 Aucun terme ne correspond à <strong>&quot;{q}&quot;</strong>. Essayez un autre
 mot-clé.
 </div>
 )}

 {filtered.map((cat) => {
 const color = CAT_COLORS[cat.cat] ||"#15294F";
 return (
 <section
 key={cat.cat}
 data-testid={`glossaire-cat-${cat.cat.replace(/\s+/g,"-")}`}
 className="bg-white border border-keyto rounded-2xl shadow-keyto-sm overflow-hidden"
 >
 <header
 className="flex items-center gap-3 px-5 md:px-6 py-3 border-b border-keyto"
 style={{ borderLeft: `4px solid ${color}` }}
 >
 <h2
 className="font-heading text-lg font-bold tracking-tight uppercase"
 style={{ color }}
 >
 {cat.cat}
 </h2>
 <span className="text-xs text-slate-400 font-mono ml-auto">
 {cat.terms.length} terme{cat.terms.length > 1 ?"s" :""}
 </span>
 </header>

 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-keyto">
 {cat.terms.map((t, i) => {
 const id = `${cat.cat}-${t.terme}`;
 const isOpen = openId === id;
 const hasMore = t.dev || t.piege || t.cas;
 return (
 <article
 key={t.terme}
 data-testid={`glossaire-term-${t.terme.replace(/\s+/g,"-")}`}
 className={`p-5 ${
 i >= 3 ?"lg:border-t lg:border-keyto" :""
 } ${i >= 2 && i < 3 ?"sm:border-t sm:border-keyto" :""}`}
 >
 <h3
 className="font-heading font-bold text-base mb-2"
 style={{ color }}
 >
 {t.terme}
 </h3>
 <p className="text-sm text-slate-700 leading-relaxed">
 {t.def}
 </p>
 {t.ex && (
 <p className="mt-3 text-xs text-slate-500 italic border-l-2 pl-2.5 py-0.5" style={{ borderColor: color }}>
 <span className="not-italic font-semibold text-slate-600">Ex. </span>
 {t.ex}
 </p>
 )}

 {hasMore && (
 <>
 <button
 data-testid={`glossaire-toggle-${t.terme.replace(/\s+/g,"-")}`}
 onClick={() => setOpenId(isOpen ? null : id)}
 className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider hover:underline transition"
 style={{ color }}
 >
 {isOpen ?"Replier" :"Approfondir"}
 {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
 </button>

 {isOpen && (
 <div className="mt-3 space-y-2.5 text-xs">
 {t.dev && (
 <div className="flex gap-2 p-2.5 rounded-md bg-slate-50 border border-keyto">
 <Lightbulb size={13} className="shrink-0 mt-0.5" style={{ color }} />
 <div>
 <p className="font-bold text-[10px] uppercase tracking-wider mb-1" style={{ color }}>Développement</p>
 <p className="text-slate-700 leading-relaxed">{t.dev}</p>
 </div>
 </div>
 )}
 {t.piege && (
 <div className="flex gap-2 p-2.5 rounded-md bg-amber-50 border border-[#A56A2E]/30">
 <AlertTriangle size={13} className="shrink-0 mt-0.5 text-[#A56A2E]" />
 <div>
 <p className="font-bold text-[10px] uppercase tracking-wider text-[#A56A2E] mb-1">Piège classique</p>
 <p className="text-slate-700 leading-relaxed">{t.piege}</p>
 </div>
 </div>
 )}
 {t.cas && (
 <div className="flex gap-2 p-2.5 rounded-md bg-[#15294F] text-white">
 <Calculator size={13} className="shrink-0 mt-0.5 text-amber-300" />
 <div>
 <p className="font-bold text-[10px] uppercase tracking-wider text-amber-300 mb-1">Mini-cas chiffré</p>
 <p className="leading-relaxed">{renderRich(t.cas,"#5AA0FF")}</p>
 </div>
 </div>
 )}
 </div>
 )}
 </>
 )}
 </article>
 );
 })}
 </div>
 </section>
 );
 })}

 {/* Footer note */}
 <div className="rounded-xl bg-[#15294F] text-white p-5 md:p-6">
 <p className="text-sm font-semibold text-[#5AA0FF] tracking-wide uppercase mb-2">
 Astuce
 </p>
 <p className="text-sm text-white/90 leading-relaxed">
 Ce glossaire est aussi imprimé en page 2 du PCG officiel téléchargeable
 — gardez-le sous la main pendant vos exercices.
 </p>
 </div>
 </div>
 );
}
